import { RbacActions } from '@supply-mover-2-0/shared';
import type { BulkActionConfig } from './types/list';

const clientUpdatePermission: Array<string> = [
	RbacActions.CLIENTS.LEADS.UPDATE.action,
	RbacActions.CLIENTS.CUSTOMERS.UPDATE.action,
];

const bulkActionTemplates: Record<string, Array<BulkActionConfig>> = {
	'lead-eligibility': [
		{
			id: 'bulk-edit',
			label: 'Bulk Edit',
			callback: 'bulkEditEligibility',
			// PD-14923 — dedicated manage action is the sole edit gate
			// (user:update does not grant it).
			permission: RbacActions.USER.LEAD_ELIGIBILITY_MANAGE.action,
		},
	],
	clients: [
		{
			id: 'add-to-list',
			label: 'Add to List',
			callback: 'addToList',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-sales-rep',
			label: 'Assign Sales Rep',
			callback: 'assignSalesRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'unassign-sales-rep',
			label: 'Unassign Sales Rep',
			callback: 'unassignSalesRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-alternate-rep',
			label: 'Assign Alternate Rep',
			callback: 'assignAlternateRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'unassign-alternate-rep',
			label: 'Unassign Alternate Rep',
			callback: 'unassignAlternateRep',
			permission: clientUpdatePermission,
		},
		{
			id: 'assign-to-group',
			label: 'Assign to Group',
			callback: 'assignToGroup',
			permission: clientUpdatePermission,
		},
		{
			id: 'remove-from-group',
			label: 'Remove from Group',
			callback: 'removeFromGroup',
			permission: clientUpdatePermission,
		},
		{
			id: 'change-lifecycle-stage',
			label: 'Change Lifecycle Stage',
			callback: 'changeLifecycleStage',
			permission: clientUpdatePermission,
		},
		{
			id: 'distribute-leads',
			label: 'Distribute Leads',
			callback: 'distributeLeads',
			permission: clientUpdatePermission,
		},
		{
			id: 'delete-clients',
			label: 'Delete Clients',
			callback: 'deleteClients',
			permission: clientUpdatePermission,
		},
		{
			id: 'undelete-clients',
			label: 'Recover Clients',
			callback: 'undeleteClients',
			permission: clientUpdatePermission,
		},
		{
			// Unified bulk-edit covers both native client columns AND
			// custom fields. Permission union (canAny) preserves both
			// prior audiences: users with client-update perm can edit
			// native columns, users with custom-field-edit perm can edit
			// custom fields, and users with both see one menu item
			// instead of two.
			id: 'bulk-edit',
			label: 'Bulk Edit',
			callback: 'bulkEdit',
			permission: [
				...clientUpdatePermission,
				RbacActions.CUSTOM_FIELDS.VALUES.EDIT.action,
			],
		},
	],
	priceContractClients: [
		{
			id: 'remove-from-contract',
			label: 'Remove from Contract',
			callback: 'removeFromContract',
			permission: RbacActions.PRICE_CONTRACTS.CLIENTS.DELETE.action,
		},
	],
	// PD-14228 — ownership detail Clients tab: bulk-remove from the ownership.
	ownershipClients: [
		{
			id: 'remove-from-ownership',
			label: 'Remove from Ownership',
			callback: 'removeFromOwnership',
			permission: clientUpdatePermission,
		},
	],
	'sourcing-requests': [
		{
			id: 'assign-user',
			label: 'Assign User',
			callback: 'assignUser',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'add-watcher',
			label: 'Add Watcher',
			callback: 'addWatcher',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'change-priority',
			label: 'Change Priority',
			callback: 'changePriority',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
		{
			id: 'update-target-date',
			label: 'Update Target Date',
			callback: 'updateTargetDate',
			permission: [RbacActions.SOURCING_REQUESTS.BULK_ASSIGN.action],
		},
	],
	priceContractItems: [
		// SAVED_PRICES.CREATE is included so sales-rep-style users who manage
		// per-client saved prices without holding the broader ITEMS.UPDATE /
		// ITEMS.DELETE can still see the bulk actions. They can only reach the
		// items list on a saved-prices contract (module config gates the tab
		// visibility), so showing these here is contract-appropriate.
		{
			id: 'update-pricing',
			label: 'Edit',
			callback: 'editPricing',
			permission: [
				RbacActions.PRICE_CONTRACTS.ITEMS.UPDATE.action,
				RbacActions.PRICE_CONTRACTS.SAVED_PRICES.CREATE.action,
			],
		},
		{
			id: 'remove-from-contract',
			label: 'Remove from Contract',
			callback: 'removeFromContract',
			permission: [
				RbacActions.PRICE_CONTRACTS.ITEMS.DELETE.action,
				RbacActions.PRICE_CONTRACTS.SAVED_PRICES.CREATE.action,
			],
		},
	],
};

export function getBulkActionTemplate(key: string): Array<BulkActionConfig> {
	return bulkActionTemplates[key] ?? [];
}
